import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: string;
  items: FAQItem[];
}

export default function HelpCenter() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleAccordion = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };

  const downloadGuide = [
    {
      platform: 'App Store (iOS)',
      icon: 'ri-apple-fill',
      color: 'from-gray-800 to-gray-900',
      steps: [
        'Open the App Store app on your iPhone or iPad',
        'Tap the "Search" tab at the bottom',
        'Type "Amagi Poker" in the search box',
        'Find the Amagi Poker - Texas Hold\'em app',
        'Tap the "Get" button, then tap "Install"',
        'Confirm the download using Face ID, Touch ID, or enter your Apple ID password',
        'Once the download is complete, tap "Open" to start playing'
      ]
    },
    {
      platform: 'Google Play (Android)',
      icon: 'ri-google-play-fill',
      color: 'from-green-600 to-teal-600',
      steps: [
        'Open the Google Play Store on your Android device',
        'Tap the search bar at the top',
        'Type "Amagi Poker" and search',
        'Find the Amagi Poker app in the search results',
        'Tap the "Install" button',
        'Wait for the app to download and install',
        'Once installed, tap "Open" or find the app icon on your home screen to launch'
      ]
    }
  ];

  const registrationMethods = [
    {
      method: 'Phone Number Registration',
      icon: 'ri-smartphone-line',
      description: 'Quickly register an account using your phone number',
      steps: [
        'Open the Amagi Poker app',
        'Tap the "Sign Up" button',
        'Select "Phone Number Registration"',
        'Enter your phone number',
        'Tap "Get Verification Code"',
        'Enter the 6-digit verification code you received',
        'Set a login password (at least 8 characters, including letters and numbers)',
        'Complete registration and start playing'
      ]
    },
    {
      method: 'Email Registration',
      icon: 'ri-mail-line',
      description: 'Create an account using your email address',
      steps: [
        'Open the app and tap "Sign Up"',
        'Select "Email Registration"',
        'Enter your email address',
        'Set a login password',
        'Tap "Send Verification Email"',
        'Open your email and click the verification link',
        'Return to the app to complete registration'
      ]
    },
    {
      method: 'Apple ID Sign In',
      icon: 'ri-apple-fill',
      description: 'iOS users can sign in with one tap using Apple ID',
      steps: [
        'On the login page, tap "Sign in with Apple"',
        'The system will automatically recognize your Apple ID',
        'Choose whether to share your email address',
        'Confirm using Face ID or Touch ID',
        'Automatically complete registration and sign in'
      ]
    },
    {
      method: 'Google Account Sign In',
      icon: 'ri-google-fill',
      description: 'Quickly sign in using your Google account',
      steps: [
        'Tap "Sign in with Google"',
        'Select the Google account you want to use',
        'Authorize the app to access basic information',
        'Automatically complete registration and sign in to the game'
      ]
    },
    {
      method: 'Facebook Sign In',
      icon: 'ri-facebook-fill',
      description: 'Sign in with one tap using your Facebook account',
      steps: [
        'Tap "Sign in with Facebook"',
        'Enter your Facebook account and password',
        'Authorize the app to access your public information',
        'Complete sign in and start playing'
      ]
    }
  ];

  const faqCategories: FAQCategory[] = [
    {
      title: 'Account Related',
      icon: 'ri-user-line',
      items: [
        {
          question: 'What should I do if I forget my password?',
          answer: 'On the login page, click "Forgot Password" and enter the phone number or email you used during registration. The system will send a verification code or link to reset your password. After receiving the verification code, enter your new password to complete the reset. If you cannot receive the verification code, please check if your phone number/email is correct, or contact customer service for assistance.'
        },
        {
          question: 'How do I appeal if my account is banned?',
          answer: 'If your account has been banned, please contact our customer service team via Telegram (@AmagiPoker). Provide your account information and reason for appeal, and we will review your appeal within 24-48 hours. Please note that using cheats, malicious score manipulation, and other violations may result in permanent bans.'
        },
        {
          question: 'How do I change my account information?',
          answer: 'After logging in, go to the "Settings" page where you can modify your nickname, avatar, personal bio, and other information. To change your linked phone number or email, you need to verify your original phone number/email first, then enter the new contact information and complete verification.'
        },
        {
          question: 'Can I delete my account?',
          answer: 'Yes. Go to "Settings" > "Account & Security" > "Delete Account" and follow the prompts to complete identity verification. Note: After account deletion, all game data, chips, VIP level, etc. will be permanently deleted and cannot be recovered. Please proceed with caution.'
        }
      ]
    },
    {
      title: 'Game Issues',
      icon: 'ri-gamepad-line',
      items: [
        {
          question: 'What should I do if I run out of chips?',
          answer: 'You can obtain chips through the following methods: 1) Claim free chips by logging in daily; 2) Complete daily tasks and achievements; 3) Participate in limited-time events; 4) Invite friends to register; 5) Purchase chip packages in the shop. New players can also receive additional starter packages.'
        },
        {
          question: 'How do I handle game crashes or lag?',
          answer: 'Please try the following solutions: 1) Ensure the app is updated to the latest version; 2) Close other background apps to free up memory; 3) Check if your network connection is stable; 4) Clear app cache (Settings > Clear Cache); 5) Restart your device and try again. If the problem persists, please contact customer service and provide your device model and system version.'
        },
        {
          question: 'How do I participate in tournaments?',
          answer: 'Click the "Tournament" button on the main interface to view currently available tournaments. Select a tournament you\'re interested in, click "Register" and pay the entry fee (chips). You\'ll receive a notification 5 minutes before the tournament starts, and you can join on time to participate. Tournament rewards will be automatically credited to your account after the tournament ends.'
        },
        {
          question: 'What is the VIP system? How do I level up?',
          answer: 'The VIP system provides exclusive privileges based on your game activity and recharge amount. You can earn VIP experience points by participating in games, completing tasks, recharging, etc. The higher your VIP level, the more privileges you enjoy, including: daily chip rewards, exclusive avatar frames, priority customer service, exclusive events, and more.'
        }
      ]
    },
    {
      title: 'Recharge & Withdrawal',
      icon: 'ri-wallet-line',
      items: [
        {
          question: 'What recharge methods are supported?',
          answer: 'We support multiple recharge methods: 1) Apple Pay (iOS users); 2) Google Play in-app purchases (Android users); 3) Credit/Debit cards; 4) PayPal; 5) Cryptocurrency (in some regions). All payments are processed through official channels and are safe and reliable.'
        },
        {
          question: 'What should I do if my recharge doesn\'t arrive?',
          answer: 'Recharges usually arrive within 1-5 minutes. If it hasn\'t arrived after 10 minutes, please check: 1) Whether the payment was successfully deducted; 2) Whether your network connection is normal; 3) Try restarting the app. If still unresolved, please contact customer service and provide your order number and payment proof.'
        },
        {
          question: 'Can I get a refund?',
          answer: 'According to App Store and Google Play policies, virtual goods generally do not support refunds once purchased. In special circumstances (such as accidental recharge, duplicate charges, etc.), please contact customer service within 48 hours, and we will assist based on the actual situation.'
        }
      ]
    },
    {
      title: 'Technical Support',
      icon: 'ri-customer-service-line',
      items: [
        {
          question: 'How do I contact customer service?',
          answer: 'You can contact us through the following methods: 1) Telegram: @AmagiPoker (recommended, fastest response); 2) In-app "Settings" > "Help & Feedback" > "Contact Customer Service"; 3) Send an email to support@amagipoker.com. Customer service hours: Monday to Sunday 9:00-24:00 (UTC+8).'
        },
        {
          question: 'What devices and system versions are supported?',
          answer: 'iOS: Requires iOS 13.0 or later, supports iPhone, iPad, and iPod touch. Android: Requires Android 6.0 or later. We recommend using newer devices for the best gaming experience.'
        },
        {
          question: 'Will my game data be lost?',
          answer: 'Your game data is automatically saved on cloud servers. As long as you log in with the same account, you can restore your game progress, chips, and VIP level on any device. We recommend linking your phone number or email to ensure account security.'
        },
        {
          question: 'How do I report cheating players?',
          answer: 'If you discover a player using cheats or engaging in other cheating behaviors, please click on that player\'s avatar in the game, select "Report", choose the reason for reporting, and submit. We will investigate within 24 hours, and once verified, we will take serious action against violating accounts.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-xl" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
            Help Center
          </h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-zinc-900 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search help content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors text-sm sm:text-base"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Download Guide */}
        <section className="mb-16 sm:mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <i className="ri-download-line text-amber-400 text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">How to Download</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {downloadGuide.map((guide, index) => (
              <div key={index} className="bg-zinc-900 rounded-2xl p-6 sm:p-8 border border-zinc-800">
                <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r ${guide.color} mb-6`}>
                  <i className={`${guide.icon} text-xl`} />
                  <span className="font-semibold">{guide.platform}</span>
                </div>
                <ol className="space-y-3">
                  {guide.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex gap-3 text-sm sm:text-base">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-semibold">
                        {stepIndex + 1}
                      </span>
                      <span className="text-gray-300 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 pt-6 border-t border-zinc-800">
                  <a
                    href={index === 0 ? 'https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770' : 'https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors cursor-pointer text-sm"
                  >
                    <span>Download Now</span>
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Registration Methods */}
        <section className="mb-16 sm:mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <i className="ri-user-add-line text-amber-400 text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Registration Methods</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {registrationMethods.map((method, index) => (
              <div key={index} className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 hover:border-amber-400/50 transition-colors">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                  <i className={`${method.icon} text-amber-400 text-2xl`} />
                </div>
                <h3 className="text-xl font-bold mb-2">{method.method}</h3>
                <p className="text-gray-400 text-sm mb-4">{method.description}</p>
                <div className="space-y-2">
                  {method.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex gap-2 text-sm">
                      <i className="ri-checkbox-circle-fill text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <i className="ri-question-line text-amber-400 text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Frequently Asked Questions</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <i className={`${category.icon} text-amber-400 text-xl`} />
                  </div>
                  <h3 className="text-xl font-bold">{category.title}</h3>
                </div>
                <div className="space-y-3">
                  {category.items.map((item, itemIndex) => {
                    const id = `faq-${categoryIndex}-${itemIndex}`;
                    const isOpen = openIndex === id;
                    return (
                      <div key={itemIndex} className="border border-zinc-800 rounded-xl overflow-hidden">
                        <button
                          onClick={() => toggleAccordion(id)}
                          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-800 transition-colors cursor-pointer"
                        >
                          <span className="font-medium text-sm sm:text-base pr-2">{item.question}</span>
                          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-amber-400 text-xl flex-shrink-0`} />
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 pt-2 text-sm text-gray-400 leading-relaxed border-t border-zinc-800">
                            {item.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Support */}
        <section className="mt-16 sm:mt-20">
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-8 sm:p-12 text-center">
            <i className="ri-customer-service-2-line text-5xl text-amber-400 mb-4" />
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">Have More Questions?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              If you haven't found the answer you're looking for, feel free to contact our customer service team anytime. We're here to help you.
            </p>
            <a
              href="https://t.me/AmagiPoker"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full font-semibold hover:scale-105 transition-transform cursor-pointer"
            >
              <i className="ri-telegram-fill text-xl" />
              <span>Contact Support</span>
            </a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-2xl font-black bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent mb-4">
            AMAGI POKER
          </div>
          <p className="text-gray-600 text-sm">© 2025 Amagi Game Inc. All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
}